import type { IAPIResult } from 'types/state.interface';
import { z } from 'zod';
import type { TTEFieldValue, TTEObject } from '@timeedit/types';

type SortField = 'ID' | 'MOD' | 'FIELD';
type SortDirection = 'ASC' | 'DESC';
export type Sort = `${SortDirection}${SortField}`;

export type TFindObjectsPayload = {
  extIds?: string[];
  typeId?: number;
  ids?: number[];
  excludeIds?: number[];
  excludeExtIds?: string[];
  fields?: {
    exactSearchFields?: Array<{
      fieldId: number;
      values: string[];
    }>;
    generalSearchFieldIds?: number[];
    searchText?: string;
  };
  modified?: {
    beginModified?: number;
    endModified?: number;
  };
  objectStatus?: 'READ' | 'RESERVE' | 'PLAN';
  abstract?: 'TRUE' | 'FALSE';
  active?: 'TRUE' | 'FALSE';
  virtual?: 'TRUE' | 'FALSE';
  limit?: number;
  page?: number;
  sort?: Sort;
  sortFieldId?: number;
  organizations?: string[];
  includeTemplateObjects?: boolean;
  includeReferenceFields?: boolean;
};

// Response of POST /objects/find, confirmed against beta
export type TObjectFindResponse = IAPIResult<TTEObject>;

// An answer with nothing in it, for callers that know the request would match nothing
export const emptyObjectFindResponse = (
  limit: number
): TObjectFindResponse => ({
  results: [],
  page: 1,
  limit,
  totalPages: 0,
  totalResults: 0,
});

// Properties the API manages itself; they never go into a create or update body
export const OBJECT_SERVER_PROPS = [
  'id',
  'history',
  'created',
  'createdBy',
  'virtual',
  'abstract',
  'color',
] as const;
export type TObjectServerProps = (typeof OBJECT_SERVER_PROPS)[number];

export type TObjectSavePayload = Omit<
  TTEObject,
  TObjectServerProps | 'fields'
> & {
  fields: TTEFieldValue<string>[];
};

// List filters; the list endpoint adds paging itself
export type TObjectListArg = Omit<TFindObjectsPayload, 'page' | 'limit'>;

// Whether a list request can match anything. The API answers both cases below with the
// unfiltered set, which is never what a table wants.
export const canMatchObjects = (arg: TObjectListArg): boolean => {
  // Having no object type to filter on should result in no objects being fetched
  if (arg.typeId == null) return false;
  // A search text with no fields to search in matches nothing
  if (
    (arg.fields?.searchText?.length ?? 0) > 0 &&
    arg.fields?.generalSearchFieldIds?.length === 0
  ) {
    return false;
  }
  return true;
};

// The envelope only; the objects inside are trusted to match TTEObject, since a schema for
// them would restate @timeedit/types, which the API already drifts from
const objectFindResponseSchema = z.object({
  results: z.array(z.looseObject({})),
  page: z.number(),
  totalPages: z.number(),
  totalResults: z.number(),
});

export const isObjectFindResponse = (
  value: unknown
): value is TObjectFindResponse =>
  objectFindResponseSchema.safeParse(value).success;
